import React from "react";
import classNames from "classnames";
import { ColumnsBlock } from "../../types/optimizely/blocks";
import ColumnContentRowItemBlock from "./ColumnContentRowItemBlock";
import { getHeaderClassNameRedesign } from "../PagePreviewBlock/helpers";
import { RedesignHeaderType } from "../PagePreviewBlock/types";

const responsiveMap: Record<number, string> = {
  1: "w-full",
  2: "w-full lg:w-1/2",
  3: "w-full lg:w-1/2 xl:w-1/3",
  4: "w-full lg:w-1/2 xl:w-1/4",
};

const widthMap: Record<string, string> = {
  "100": "w-full",
  "50": "w-full lg:w-1/2",
  "33": "w-full lg:w-1/2 xl:w-1/3",
  "25": "w-full lg:w-1/2 xl:w-1/4",
};

const ColumnContentRowBlock: React.FC<ColumnsBlock> = ({
  title,
  boldTitle,
  headerType,
  items,
  columnAlignment,
  hideTopSpacing,
  hideBottomSpacing,
  sectionId,
}) => {
  const columnItems = items?.expandedValue || [];
  const containerClasses = classNames(
    "container z-40",
    hideTopSpacing?.value ? "pt-0" : "pt-8 md:pt-12",
    hideBottomSpacing?.value ? "pb-0" : "pb-8 md:pb-12"
  );

  const getColumnVariant = (alignmentValue?: string) => {
    if (!alignmentValue) {
      return columnItems.length;
    }
    return alignmentValue.split("-").length;
  };

  const variant = getColumnVariant(columnAlignment?.value);
  const imageHeightClass =
    variant <= 2 ? "h-[423px]" : variant === 3 ? "h-[275px]" : "h-[198px]";
  const fillerItems = Array.from(
    { length: variant - columnItems.length },
    (_, i) => i
  );
  const displayedItems = columnItems.slice(0, variant);
  const headerTypeValue = headerType?.value ?? RedesignHeaderType.Big;
  const shouldRenderBoldHeader = headerTypeValue === RedesignHeaderType.Big;

  const getElementWidth = (index?: number) => {
    if (columnAlignment?.value === "") {
      return (
        responsiveMap[columnItems.length] ||
        "w-full lg:w-1/2 xl:w-1/3 2xl:w-1/4"
      );
    }

    const alignmentValues = columnAlignment?.value?.split("-");

    return (
      widthMap[alignmentValues[index]] || "w-full lg:w-1/2 xl:w-1/3 2xl:w-1/4"
    );
  };

  return (
    <section id={sectionId?.value || undefined}>
      <div className={classNames({ "container": !shouldRenderBoldHeader })}>
        {title?.value && (
          <h2 className={getHeaderClassNameRedesign(headerTypeValue)}>
            <span>
              {title?.value}
              {shouldRenderBoldHeader && (
                <span className="font-bold">
                  {" " + boldTitle?.value}
                </span>
              )}
            </span>
          </h2>
        )}
      </div>
      <div className={containerClasses}>
        {columnItems.length > 0 && (
          <div className="flex flex-col lg:flex-row gap-4 flex-nowrap">
            {displayedItems.map((item, index) => {
              const elementWidth = getElementWidth(index);
              return (
                <div
                  key={`column-content-row-item-${index}`}
                  className={elementWidth}
                >
                  <ColumnContentRowItemBlock
                    item={item}
                    imageHeightClass={imageHeightClass}
                  />
                </div>
              );
            })}
            {fillerItems.length > 0 &&
              fillerItems.map((_, fillerIndex) => {
                const fillerElementWidth = getElementWidth(fillerIndex);
                return (
                  <div
                    key={`column-content-row-item-filler-${fillerIndex}`}
                    className={fillerElementWidth + " xl:block hidden"}
                  ></div>
                );
              })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ColumnContentRowBlock;
